import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { getGRNByProduct } from '../services/api';

const GRNProductHistory = ({ open, onClose, product }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    serial_number: ''
  });

  useEffect(() => {
    if (open && product?.id) {
      fetchHistory();
    }
  }, [open, product?.id]);

  const fetchHistory = async (filterParams = filters) => {
    if (!product?.id) return;
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (filterParams.start_date) params.append('start_date', filterParams.start_date);
      if (filterParams.end_date) params.append('end_date', filterParams.end_date);
      if (filterParams.serial_number) params.append('serial_number', filterParams.serial_number);
      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await getGRNByProduct(product.id, query);
      if (response.success) {
        setHistory(response.data || []);
      } else {
        setError(response.message || 'Failed to load GRN history');
      }
    } catch (err) {
      setError(err.message || 'Failed to load GRN history');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => fetchHistory(filters);

  const handleClearFilters = () => {
    const cleared = { start_date: '', end_date: '', serial_number: '' };
    setFilters(cleared);
    fetchHistory(cleared);
  };

  if (!product) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        GRN History — {product.name} ({product.product_code})
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Start Date" type="date"
              value={filters.start_date}
              onChange={(e) => setFilters(p => ({ ...p, start_date: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="End Date" type="date"
              value={filters.end_date}
              onChange={(e) => setFilters(p => ({ ...p, end_date: e.target.value }))}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Serial Number"
              value={filters.serial_number}
              onChange={(e) => setFilters(p => ({ ...p, serial_number: e.target.value }))} />
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" gap={1}>
              <Button variant="contained" size="small" onClick={handleApplyFilters}>Apply</Button>
              <Button variant="outlined" size="small" onClick={handleClearFilters}>Clear</Button>
            </Box>
          </Grid>
        </Grid>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
        ) : history.length === 0 ? (
          <Typography color="textSecondary" align="center" py={3}>
            No GRN records found for this product.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>GRN #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell align="right">Qty Received</TableCell>
                  <TableCell>Serial Numbers</TableCell>
                  <TableCell>Received By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map(grn => {
                  const detail = (grn.details || []).find(d => d.product_id === product.id);
                  const serials = Array.isArray(detail?.serial_numbers) ? detail.serial_numbers : [];
                  return (
                    <TableRow key={grn.grn_id}>
                      <TableCell><strong>{grn.grn_number}</strong></TableCell>
                      <TableCell>{grn.received_date}</TableCell>
                      <TableCell>{grn.received_time}</TableCell>
                      <TableCell>{grn.supplier?.name || '—'}</TableCell>
                      <TableCell align="right">{detail?.quantity_received || 0}</TableCell>
                      <TableCell>
                        {serials.length > 0 ? serials.map(s => (
                          <Chip key={s} label={s} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                        )) : '—'}
                      </TableCell>
                      <TableCell>{grn.cashier_name}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GRNProductHistory;
